/**
 * Single source of truth for Fitxess legal copy.
 *
 * Rendered by /privacy (PrivacyPolicy.jsx) and /terms (TermsOfUse.jsx) via
 * components/legal/LegalContent.jsx. Keep in sync with /app/website/privacy.html
 * and /app/website/terms.html.
 *
 * Block shapes:
 *   { type: 'heading', text: string }
 *   { type: 'paragraph', text: string }
 *   { type: 'list', items: string[] }
 */

export const privacyPolicy = {
  title: 'Privacy Policy',
  lastUpdated: 'April 24, 2026',
  blocks: [
    { type: 'paragraph', text: 'Fitxess LLC' },
    { type: 'paragraph', text: 'Effective Date: April 24, 2026' },
    {
      type: 'paragraph',
      text:
        'Fitxess LLC (\u201CFitxess,\u201D \u201Cwe,\u201D \u201Cour,\u201D or \u201Cus\u201D) operates the Fitxess mobile application (the \u201CApp\u201D). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the App.',
    },
    {
      type: 'paragraph',
      text: 'By using Fitxess, you agree to the collection and use of information in accordance with this policy.',
    },

    { type: 'heading', text: '1. Information We Collect' },
    { type: 'paragraph', text: 'We may collect the following categories of information:' },
    { type: 'heading', text: 'a. Account Information' },
    {
      type: 'list',
      items: [
        'Email address',
        'Name or username (if provided)',
        'Authentication data when signing in via Apple, Google, or email/password',
      ],
    },
    { type: 'heading', text: 'b. User-Generated Content' },
    {
      type: 'list',
      items: [
        'Food logs, meals, and dietary entries',
        'Nutrition data (calories, macros, goals)',
        'Preferences, settings, and saved plans',
        'Grocery list data',
      ],
    },
    { type: 'heading', text: 'c. Usage and Device Information' },
    {
      type: 'list',
      items: [
        'App interactions and usage behavior',
        'Device type, operating system, and performance data',
        'Crash logs and diagnostics',
      ],
    },
    { type: 'heading', text: 'd. Subscription Information' },
    {
      type: 'list',
      items: ['Subscription status (active, expired, trial)', 'Product identifiers'],
    },
    {
      type: 'paragraph',
      text: 'Note: Payment details are processed by Apple and are not collected or stored by Fitxess.',
    },

    { type: 'heading', text: '2. How We Use Your Information' },
    { type: 'paragraph', text: 'We use your information to:' },
    {
      type: 'list',
      items: [
        'Provide, operate, and maintain the App',
        'Generate personalized meal plans and nutrition insights',
        'Process and manage subscriptions',
        'Improve features, performance, and user experience',
        'Monitor usage trends and diagnose issues',
        'Respond to customer support inquiries',
      ],
    },

    { type: 'heading', text: '3. Legal Basis for Processing (if applicable)' },
    {
      type: 'paragraph',
      text: 'If you are located in regions requiring a legal basis (such as the EEA), we process your data based on:',
    },
    {
      type: 'list',
      items: [
        'Performance of a contract (providing the app)',
        'Legitimate interests (improving the app)',
        'Consent (where applicable)',
      ],
    },

    { type: 'heading', text: '4. Payments and Subscriptions' },
    {
      type: 'paragraph',
      text: 'All purchases and payments are handled through Apple\u2019s in-app purchase system.',
    },
    {
      type: 'list',
      items: [
        'We do not collect or store payment information',
        'Apple may share limited transaction data (such as subscription status)',
      ],
    },

    { type: 'heading', text: '5. Data Storage and Retention' },
    {
      type: 'list',
      items: [
        'Data is stored securely using cloud services, including MongoDB Atlas',
        'We retain data only as long as necessary to provide services or comply with legal obligations',
        'When no longer needed, data is deleted or anonymized',
      ],
    },

    { type: 'heading', text: '6. Data Sharing and Disclosure' },
    { type: 'paragraph', text: 'We do not sell or rent your personal data.' },
    { type: 'paragraph', text: 'We may share information only in the following cases:' },
    {
      type: 'list',
      items: [
        'With service providers (hosting, analytics) necessary to operate the app',
        'To comply with legal obligations',
        'To protect rights, safety, or prevent fraud',
      ],
    },
    { type: 'paragraph', text: 'All third parties are required to safeguard your data.' },

    { type: 'heading', text: '7. Data Security' },
    {
      type: 'paragraph',
      text: 'We implement reasonable administrative, technical, and physical safeguards to protect your information. However, no system is completely secure.',
    },

    { type: 'heading', text: '8. Your Rights' },
    { type: 'paragraph', text: 'Depending on your location, you may have the right to:' },
    {
      type: 'list',
      items: [
        'Access your data',
        'Correct inaccurate information',
        'Request deletion of your data',
        'Withdraw consent where applicable',
      ],
    },
    { type: 'paragraph', text: 'To exercise these rights, contact: karis@fitxess.com' },

    { type: 'heading', text: '9. Account Deletion' },
    { type: 'paragraph', text: 'You may delete your account at any time within the App.' },
    { type: 'paragraph', text: 'Upon deletion:' },
    {
      type: 'list',
      items: [
        'Your personal data will be removed or anonymized',
        'Some data may be retained as required by law',
      ],
    },
    { type: 'paragraph', text: 'If you need assistance, contact: karis@fitxess.com' },

    { type: 'heading', text: '10. Children\u2019s Privacy' },
    { type: 'paragraph', text: 'Fitxess is intended for users aged 13 and older.' },
    {
      type: 'paragraph',
      text: 'We do not knowingly collect personal data from children under 13. If such data is discovered, it will be deleted.',
    },

    { type: 'heading', text: '11. International Data Transfers' },
    {
      type: 'paragraph',
      text: 'Your information may be processed and stored in servers located outside your country, including the United States.',
    },

    { type: 'heading', text: '12. Changes to This Privacy Policy' },
    {
      type: 'paragraph',
      text: 'We may update this Privacy Policy periodically. Changes will be reflected with an updated \u201CEffective Date.\u201D',
    },
    { type: 'paragraph', text: 'Continued use of the App indicates acceptance of the updated policy.' },

    { type: 'heading', text: '13. Contact Information' },
    { type: 'paragraph', text: 'If you have questions about this Privacy Policy, contact:' },
    { type: 'paragraph', text: 'Fitxess LLC\nkaris@fitxess.com' },
  ],
};

export const termsOfUse = {
  title: 'Terms of Use',
  lastUpdated: 'April 24, 2026',
  blocks: [
    { type: 'paragraph', text: 'Fitxess LLC' },
    { type: 'paragraph', text: 'Effective Date: April 24, 2026' },
    { type: 'paragraph', text: 'By accessing or using Fitxess, you agree to these Terms of Use.' },

    { type: 'heading', text: '1. Description of Service' },
    {
      type: 'paragraph',
      text: 'Fitxess provides meal planning, nutrition tracking, and related tools for informational purposes only.',
    },

    { type: 'heading', text: '2. No Medical Advice' },
    { type: 'paragraph', text: 'Fitxess is not a medical or healthcare provider.' },
    {
      type: 'list',
      items: [
        'Information is for general guidance only',
        'Not a substitute for professional advice',
        'Consult a qualified professional for medical or dietary concerns',
      ],
    },

    { type: 'heading', text: '3. User Accounts' },
    {
      type: 'list',
      items: [
        'You are responsible for maintaining your account credentials',
        'You agree to provide accurate information',
        'You must not share your account',
      ],
    },

    { type: 'heading', text: '4. Subscriptions' },
    { type: 'paragraph', text: 'Fitxess offers auto-renewing subscriptions (Fitxess Pro) through the Apple App Store and Google Play.' },
    { type: 'heading', text: 'Pricing:' },
    { type: 'paragraph', text: 'The current price is shown on the subscription screen at the time of purchase, in your local currency. Prices may vary by region and are subject to change.' },
    { type: 'heading', text: 'Billing Terms:' },
    {
      type: 'list',
      items: [
        'Payment is charged to your Apple ID or Google Play account at confirmation',
        'Subscription renews automatically unless canceled at least 24 hours before renewal',
        'Renewal is charged within 24 hours before the period ends',
        'Manage or cancel subscriptions in your App Store or Google Play account settings',
      ],
    },

    { type: 'heading', text: '5. Free Trials (if applicable)' },
    { type: 'paragraph', text: 'If a free trial is offered:' },
    { type: 'list', items: ['It converts to a paid subscription unless canceled before the trial ends'] },

    { type: 'heading', text: '6. Payments' },
    {
      type: 'paragraph',
      text: 'All billing is handled by Apple. Fitxess does not store payment information.',
    },

    { type: 'heading', text: '7. Refunds' },
    { type: 'paragraph', text: 'Refunds are handled by Apple and subject to their policies.' },

    { type: 'heading', text: '8. Acceptable Use' },
    { type: 'paragraph', text: 'You agree not to:' },
    {
      type: 'list',
      items: [
        'Use the app for unlawful purposes',
        'Attempt to reverse engineer or disrupt the app',
        'Abuse or exploit the service',
      ],
    },

    { type: 'heading', text: '9. Termination' },
    {
      type: 'paragraph',
      text: 'We may suspend or terminate access if you violate these terms.',
    },

    { type: 'heading', text: '10. Disclaimer of Warranties' },
    {
      type: 'paragraph',
      text: 'The app is provided \u201Cas is\u201D without warranties of any kind.',
    },

    { type: 'heading', text: '11. Limitation of Liability' },
    {
      type: 'paragraph',
      text: 'Fitxess LLC is not liable for any indirect, incidental, or consequential damages resulting from use of the app.',
    },

    { type: 'heading', text: '12. Intellectual Property' },
    {
      type: 'paragraph',
      text: 'All content, branding, and software are owned by Fitxess LLC and may not be used without permission.',
    },

    { type: 'heading', text: '13. Changes to Terms' },
    {
      type: 'paragraph',
      text: 'We may update these Terms at any time. Continued use means acceptance.',
    },

    { type: 'heading', text: '14. Governing Law' },
    {
      type: 'paragraph',
      text: 'These Terms are governed by applicable laws in your jurisdiction.',
    },

    { type: 'heading', text: '15. Contact' },
    { type: 'paragraph', text: 'For questions:' },
    { type: 'paragraph', text: 'Fitxess LLC\nkaris@fitxess.com' },
  ],
};

export const sourcesDisclaimer = {
  title: 'Sources & Disclaimer',
  lastUpdated: 'February 1, 2026',
  blocks: [
    { type: 'heading', text: 'Medical & Health Disclaimer' },
    {
      type: 'paragraph',
      text:
        'Fitxess provides general wellness, fitness, and nutrition information for educational purposes only. It is not medical advice and is not intended to diagnose, treat, cure, or prevent any disease or medical condition. Calorie and macronutrient targets are estimates based on published formulas and the information you provide; individual needs vary. Consult a qualified healthcare provider or registered dietitian before changing your diet, exercise, or health routine \u2014 especially if you are pregnant or nursing, under 18, or have a medical condition.',
    },

    { type: 'heading', text: 'How Fitxess Calculates Your Plan' },
    {
      type: 'paragraph',
      text:
        'We estimate your resting energy (BMR) using the Mifflin-St Jeor equation, then multiply by an activity factor and add a small amount for the workouts you log to estimate your daily calories (TDEE). We then adjust calories based on your goal (for example, a moderate deficit for fat loss or a surplus for muscle gain), while applying minimum calorie floors to avoid extremely low calorie targets. Protein is set from your bodyweight and goal, and the remaining calories are split between carbohydrates and fat within evidence-based ranges.',
    },
    {
      type: 'list',
      items: [
        'Resting energy (BMR): estimated with the Mifflin-St Jeor equation using your sex, age, height, and weight.',
        'Daily calories (TDEE): your BMR multiplied by an activity factor, plus a small adjustment for the workouts you log.',
        'Goal adjustment: calories are increased or decreased based on the goal you select.',
        'Calorie floors: calorie targets are kept at or above minimum planning floors of 1,200 kcal for women and 1,500 kcal for men.',
        'Protein: set from your bodyweight and goal.',
        'Fat: set within an evidence-based percentage range for your goal.',
        'Carbohydrates: calculated from the remaining calories.',
        'Macro-to-calorie conversions use the standard values of 4 calories per gram of protein, 4 per gram of carbohydrate, and 9 per gram of fat.',
      ],
    },

    { type: 'heading', text: 'Sources & References' },
    {
      type: 'paragraph',
      text:
        'Fitxess\u2019s estimates and general nutrition guidance are based on the following published sources:',
    },

    { type: 'heading', text: 'Calculation Methodology' },
    {
      type: 'links',
      items: [
        {
          label:
            'Mifflin MD, St Jeor ST, et al. A new predictive equation for resting energy expenditure in healthy individuals. Am J Clin Nutr. 1990.',
          url: 'https://pubmed.ncbi.nlm.nih.gov/2305711/',
        },
        {
          label: 'FAO/WHO/UNU. Human Energy Requirements, 2004.',
          url: 'https://www.fao.org/4/y5686e/y5686e00.htm',
        },
      ],
    },

    { type: 'heading', text: 'Macronutrient & Energy References' },
    {
      type: 'links',
      items: [
        {
          label:
            'National Academies. Dietary Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids, Protein, and Amino Acids.',
          url: 'https://nap.nationalacademies.org/catalog/10490/',
        },
        {
          label:
            'FAO. Food energy \u2014 methods of analysis and conversion factors, 2003.',
          url: 'https://www.fao.org/3/y5022e/y5022e00.htm',
        },
        {
          label:
            'U.S. Departments of Agriculture & Health and Human Services. Dietary Guidelines for Americans.',
          url: 'https://www.dietaryguidelines.gov/',
        },
      ],
    },

    { type: 'heading', text: 'Food & Nutrition Data' },
    {
      type: 'paragraph',
      text:
        'Nutrition values for foods you search or scan are provided by the USDA FoodData Central database and the FatSecret Platform API. Values are estimates and may vary by brand, preparation, and portion size.',
    },
    {
      type: 'links',
      items: [
        {
          label: 'USDA FoodData Central.',
          url: 'https://fdc.nal.usda.gov/',
        },
        {
          label: 'FatSecret Platform API.',
          url: 'https://platform.fatsecret.com/',
        },
      ],
    },
    {
      type: 'paragraph',
      text: 'Questions about our sources or methodology? Contact karis@fitxess.com.',
    },
  ],
};

